import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getFlashAccommodationDate, getTwinFlashAccommodation } from './addBundlePriceToTourOffer'

/** Returns undefined when a bundled tour cannot be matched to a bookable hotel package. */
export default function generateTourBundleAccommodationItems(
  tourItems: Array<App.Checkout.TourV2Item>,
  bundle: App.Tours.Bundle | undefined,
  hotel: App.HotelOffer | undefined,
): Array<App.Checkout.InstantBookingLEHotelItem> | undefined {
  const bundledTourItems = tourItems.filter(item => item.ltleBundleId)
  if (!bundledTourItems.length) return []
  if (!bundle || bundledTourItems.some(item => item.ltleBundleId !== bundle.id)) return undefined
  if (bundledTourItems.some(item => item.purchasableOption.ltleBundleId !== item.ltleBundleId || item.purchasableOption.bundledPrice === undefined)) return undefined

  const accommodation = getTwinFlashAccommodation(bundle)
  if (!accommodation || !hotel || hotel.id !== accommodation.offerId || !Number.isInteger(accommodation.nights) || accommodation.nights < 1) return undefined

  const hotelPackage = hotel.packages.find(pkg =>
    (pkg.id === accommodation.packageId || pkg.lePackageId === accommodation.packageId) && pkg.duration === accommodation.nights,
  )
  if (!hotelPackage?.roomRate?.id) return undefined
  const checkInDates = bundledTourItems
    .map(item => getFlashAccommodationDate(bundle, item.startDate)?.checkInDate)
    .filter((date): date is string => !!date && moment.utc(date, ISO_DATE_FORMAT, true).isValid())
  if (checkInDates.length !== bundledTourItems.length) return undefined
  const roomsByDate = new Map<App.Tours.BundleDate, number>()
  for (const item of bundledTourItems) {
    const date = getFlashAccommodationDate(bundle, item.startDate)
    if (!date || date.available !== true || !Number.isInteger(date.availableRooms) || (date.availableRooms ?? 0) < 1) return undefined
    const requestedRooms = (roomsByDate.get(date) ?? 0) + 1
    if (requestedRooms > (date.availableRooms ?? 0)) return undefined
    roomsByDate.set(date, requestedRooms)
  }

  return bundledTourItems.map((item, index) => ({
    itemId: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    transactionKey: uuidV4(),
    offerId: hotel.id,
    tourBundleId: bundle.id,
    packageId: hotelPackage.id,
    roomRateId: hotelPackage.roomRate.id,
    cancellationPolicy: hotelPackage.cancellationOptionPolicy,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    duration: accommodation.nights,
    checkIn: checkInDates[index],
    checkOut: moment.utc(checkInDates[index], ISO_DATE_FORMAT).add(accommodation.nights, 'days').format(ISO_DATE_FORMAT),
    occupancy: item.occupancy,
    bundledItemIds: [item.itemId],
    total: 0,
  }))
}
